import React, { useContext, useEffect, useRef, useState } from 'react';
import { array, bool, string } from 'prop-types';
import { TextStyled } from '@nike/nike-design-system-components';
import clsx from 'clsx';
import { Close } from '@nike/nike-design-system-icons';
import {
  ButtonStyled,
  ListStyled,
  RecentSearchItemStyled,
  SectionStyled,
  StackStyled,
} from './styles';
import { BODEGA_DEFAULTS, TYPE_AHEAD, POPULAR_SEARCH_TERMS_FLAG, NIKE_AI } from '../../constants';
import RecentlyViewed from '../RecentlyViewed';
import VisualSearchPopularTerms from '../VisualSearchPopularTerms';
import VisualSearchTopSuggestions from '../VisualSearchTopSuggestions';
import VisualSearchProducts from '../VisualSearchProducts';
import useBodega from './../../utils/hooks/bodegaHook';
import useExperiment from '../../utils/hooks/experimentHook';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import useSelectedSuggestion from '../../utils/hooks/selectedSuggestionHook';
import Link from '../Link';
import { StyledListItem } from '../VisualSearchPopularTerms/styles';
import useLocale from '../../utils/hooks/localeHook';
import useLocalStorage from '../../utils/hooks/localStorageHook';
import { AnalyticsContext } from '../../context/analyticsContext';
import { getSearchUrl } from '../../utils/url';
import NikeAISearchSection from '../NikeAISearchSection';

/**
 * `VisualSearchResults` is a React component that displays a list of search results links.
 * The list defaults to show links for the popular terms, but the view is similar once the top suggestion data is supplied.
 * This component will also fetch and display recent search results, if they exist in browser local storage.
 * This is the view for the search results links as per the design [here](https://www.figma.com/file/pz26hPJZ7fLWyRzO2jQV0I/Nike.com-%E2%80%93-Global-Nav-3.0?type=design&node-id=3149-36998&mode=design&t=e4jbdXs4Rvi98tVn-0).
 *
 * @component
 * @param {Object} props - The properties that define the component's behavior and display.
 * @param {boolean} props.isOpened - A boolean indicating whether the search results are currently displayed.
 * @param {Array} props.searchResultsData - An array of search results data. Each item is an object with `searchTerm` and `urlPath` properties.
 * @param {string} props.searchTerm - The current search term.
 *
 * @returns {React.Element} A list of search results links. Each link is a `VisualSearchSuggestions` or `VisualSearchDynamicSuggestions` component that displays a search term.
 * If there are no search results, the component returns null.
 *
 * `VisualSearchPopularTerms` is rendered before the user has searched for a term, and presents the eight most popular search terms by locale.
 * `VisualSearchTopSuggestions` is rendered when a user has searched for a term and the top results were returned.
 * `VisualSearchProducts` is rendered when the `showVisualSearch` prop is true, indicating that type ahead is enabled.
 *
 */
const VisualSearchResults = ({ isOpened, searchResultsData = [], searchTerm }) => {
  const searchResultsRef = useRef(null);
  const { selectedSuggestion, setSelectedSuggestion } = useSelectedSuggestion(searchResultsData);
  const { POPULAR_SEARCH_TERMS, RECENT_SEARCHES, TOP_SUGGESTIONS } = BODEGA_DEFAULTS;
  const showPopularSearchTerms = useFeatureFlags(POPULAR_SEARCH_TERMS_FLAG);
  const isNikeAiEnabled = useFeatureFlags(NIKE_AI);
  const showVisualSearch = useFeatureFlags(TYPE_AHEAD);
  const hasSearchData = !!searchResultsData.length && showVisualSearch;
  const experimentResult = useExperiment([{ name: 'global_nav_search_recently_viewed' }]);
  const [recentlyViewedExperiment, setRecentlyViewedExperiment] = useState('CONTROL');

  const { analyticsClient } = useContext(AnalyticsContext);
  const locale = useLocale();
  const popularTermsTitle = useBodega(
    'nav_header_label_popular_search_terms',
    POPULAR_SEARCH_TERMS,
  );
  const recentSearchesTitle = useBodega('nav_header_label_recent_searches', RECENT_SEARCHES);
  const topSuggestionsTitle = useBodega('nav_header_label_search_topSuggestions', TOP_SUGGESTIONS);
  const searchTitle = hasSearchData
    ? topSuggestionsTitle
    : showPopularSearchTerms && popularTermsTitle;

  const containerClass = clsx(
    `search-results-link-container`,
    hasSearchData && `has-search-data`,
    isOpened && `is-opened`,
  );

  const [storedSearches, setStoredSearches] = useState([]);
  const [getRecentSearches, , removeRecentSearch] = useLocalStorage('search:recent');

  const updateStoredSearches = () => {
    const recentSearches = getRecentSearches();
    const newStoredSearches = recentSearches?.length > 0 ? recentSearches.slice(0, 5) : [];

    if (JSON.stringify(storedSearches) !== JSON.stringify(newStoredSearches)) {
      setStoredSearches(newStoredSearches);
    }
  };

  useEffect(() => {
    if (isOpened) {
      updateStoredSearches();
    }
  }, [isOpened]);

  useEffect(() => {
    if (experimentResult && experimentResult?.locations?.length > 0) {
      const experiment = experimentResult?.locations.find(
        item => item.name === 'global_nav_search_recently_viewed',
      );
      if (experiment?.content?.experience) {
        setRecentlyViewedExperiment(experiment.content.experience);
      }
    }
  }, [experimentResult]);

  const handleDeleteRecentSearch = search => {
    removeRecentSearch(search);
    updateStoredSearches();
    analyticsClient?.clickSearchRecentDelete({ search: search });
  };

  const renderRecentSearches = () => {
    if (storedSearches?.length) {
      return (
        <StackStyled>
          <TextStyled Component="p" appearance="body2" color="secondary">
            {recentSearchesTitle}
          </TextStyled>
          <ListStyled role="listbox">
            {storedSearches.map((search, index) => (
              <RecentSearchItemStyled key={index}>
                <StyledListItem data-testid="recent-search-item" key={search}>
                  <Link
                    href={getSearchUrl({
                      locale,
                      searchTerm: search,
                      query: search,
                      path: 'w',
                    })}
                    onClick={() => {
                      analyticsClient?.clickSearchRecentSubmit({ path: search });
                    }}
                    rel="nofollow"
                  >
                    <TextStyled Component="p" appearance="title4">
                      {search}
                    </TextStyled>
                  </Link>
                </StyledListItem>
                <ButtonStyled onClick={() => handleDeleteRecentSearch(search)} type="button">
                  <Close />
                </ButtonStyled>
              </RecentSearchItemStyled>
            ))}
          </ListStyled>
        </StackStyled>
      );
    }
    return null;
  };

  const handleAskNikeAIClick = () => {
    // Track analytics if available
    if (analyticsClient?.clickAskNikeAI) {
      analyticsClient.clickAskNikeAI({ source: 'search_results', query: searchTerm });
    }

    // Toggle Nike AI using controller (same as FAB)
    if (window.webShellClient?.__WS_INTERNAL__?.nikeAI?.toggle) {
      window.webShellClient.__WS_INTERNAL__.nikeAI.toggle();
    }
  };

  const renderStyledSection = () => {
    return (
      <StackStyled>
        <TextStyled
          Component="p"
          appearance="body2"
          color="secondary"
          data-testid="visual-search-results-title"
        >
          {searchTitle}
        </TextStyled>
        {hasSearchData ? (
          <VisualSearchTopSuggestions
            searchResultsData={searchResultsData}
            searchResultsRef={searchResultsRef}
            searchTerm={searchTerm}
            selectedSuggestion={selectedSuggestion}
            setSelectedSuggestion={setSelectedSuggestion}
          />
        ) : (
          <>{showPopularSearchTerms && <VisualSearchPopularTerms />}</>
        )}
      </StackStyled>
    );
  };

  const renderRecentsSection = () => {
    if (!hasSearchData) {
      if (recentlyViewedExperiment === 'VARIANT1') {
        return (
          <>
            {renderRecentSearches()}
            <RecentlyViewed />
          </>
        );
      } else if (recentlyViewedExperiment === 'VARIANT2') {
        return (
          <>
            <RecentlyViewed />
            {renderRecentSearches()}
          </>
        );
      } else {
        return renderRecentSearches();
      }
    }
    return null;
  };

  return (
    <SectionStyled aria-label={searchTitle} data-testid="visual-search-results-container">
      {isNikeAiEnabled && (
        <NikeAISearchSection onAskNikeAIClick={handleAskNikeAIClick} searchQuery={searchTerm} />
      )}
      <div className={containerClass}>
        {renderStyledSection()}
        {renderRecentsSection()}
        {showVisualSearch && (
          <VisualSearchProducts
            searchResultsRef={searchResultsRef}
            selectedSuggestion={selectedSuggestion}
          />
        )}
      </div>
    </SectionStyled>
  );
};

export default VisualSearchResults;

VisualSearchResults.propTypes = {
  isOpened: bool,
  searchResultsData: array,
  searchTerm: string,
};
